import { gql, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import * as types from '../types';
import { showRescueGwaVariant } from '../views/RescueLanding/RescueBanner';

export const rescueCheckoutPrefillQuery = gql`
  query rescueCheckoutPrefill {
    currentUser {
      id
      email
      firstName
      lastName
      billingAccount {
        address {
          line1
          line2
          city
          state
          zip
          country
          phone
        }
      }
      chips {
        ... on AssignedChip {
          id
          latestContactHistoryEntry {
            id
            contactInfo {
              firstName
              lastName
              primaryPhone
              line1
              line2
              city
              state
              zipcode
              country
            }
          }
        }
      }
    }
  }
`;

interface ChipContactInfo {
  firstName: string;
  lastName?: string | null;
  primaryPhone?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country: string;
}

export interface RescueCheckoutPrefillData {
  currentUser?: {
    id: string;
    email: string;
    firstName: string;
    lastName?: string | null;
    billingAccount?: {
      address?: {
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        state?: string | null;
        zip?: string | null;
        country?: string | null;
        phone?: string | null;
      } | null;
    } | null;
    chips?: Array<{ latestContactHistoryEntry?: { contactInfo: ChipContactInfo } | null }>;
  };
}

/** What the stripe email and address elements open with. Stripe applies these on mount only. */
export interface StripeCheckoutPrefill {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: {
    line1: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  };
}

const present = (value: string | null | undefined) => value || undefined;

/**
 * The prefill for a signed-in adopter: email from the account, and name, phone and address from the
 * adoption record the rescue filed with the chip, falling back to the account profile and its
 * billing address for whatever the record lacks. An address outside the shop's country is dropped,
 * since the address element only accepts this market's country.
 */
export function buildRescueCheckoutPrefill(
  data: RescueCheckoutPrefillData | undefined,
  country: string,
): StripeCheckoutPrefill | undefined {
  const user = data?.currentUser;
  if (!user) {
    return undefined;
  }

  const inCountry = (value: string | null | undefined) => value?.toUpperCase() === country.toUpperCase();
  const contact = (user.chips ?? [])
    .map((chip) => chip.latestContactHistoryEntry?.contactInfo)
    .find((info): info is ChipContactInfo => !!info && inCountry(info.country));
  const billingAddress = user.billingAccount?.address;

  const address = contact?.line1
    ? {
        line1: contact.line1,
        line2: present(contact.line2),
        city: present(contact.city),
        state: present(contact.state),
        postal_code: present(contact.zipcode),
      }
    : billingAddress?.line1 && inCountry(billingAddress.country)
      ? {
          line1: billingAddress.line1,
          line2: present(billingAddress.line2),
          city: present(billingAddress.city),
          state: present(billingAddress.state),
          postal_code: present(billingAddress.zip),
        }
      : undefined;

  return {
    email: present(user.email),
    firstName: present(contact?.firstName) ?? present(user.firstName),
    lastName: present(contact?.lastName) ?? present(user.lastName),
    phone: present(contact?.primaryPhone) ?? present(billingAddress?.phone),
    address,
  };
}

/**
 * Checkout prefill for adopters who are signed in. Everyone else - signed out, or signed in outside
 * the rescue context - skips the query and gets the blank form they had before.
 *
 * `loading` is exposed so the form can wait for it: stripe reads default values only when an element
 * mounts, so a form that mounted first would never show them. `errorPolicy: 'all'` keeps the profile
 * fields when the chip half of the query fails.
 */
export default function useRescueCheckoutPrefill(): { prefill?: StripeCheckoutPrefill; loading: boolean } {
  const signedIn = useSelector((state: types.AppState) => !!state.session);
  const { country } = useLocale();
  const { data, loading } = useQuery<RescueCheckoutPrefillData>(rescueCheckoutPrefillQuery, {
    skip: !signedIn || !showRescueGwaVariant(),
    errorPolicy: 'all',
  });

  return { prefill: buildRescueCheckoutPrefill(data, country), loading };
}
